import { safeSetItem } from "@/lib/storage/safe-local";
import { useCallback, useEffect, useState } from "react";

export type ThemeChoice = "light" | "dark" | "system";

export const THEME_STORAGE_KEY = "theme";
const THEME_EVENT = "cb16:theme";

/** Routes that keep the original light presentation (Admin console is frozen). */
export function isThemeExcludedPath(pathname: string): boolean {
  return pathname.startsWith("/admin");
}

export function readStoredTheme(): ThemeChoice {
  if (typeof window === "undefined") return "system";
  const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
  return raw === "light" || raw === "dark" || raw === "system" ? raw : "system";
}

export function prefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function resolveTheme(choice: ThemeChoice): "light" | "dark" {
  return choice === "system" ? (prefersDark() ? "dark" : "light") : choice;
}

/** Applies (or clears) the dark class + data-theme attribute on <html>. */
export function applyTheme(choice: ThemeChoice, pathname?: string) {
  if (typeof document === "undefined") return;
  const path = pathname ?? window.location.pathname;
  const resolved = isThemeExcludedPath(path) ? "light" : resolveTheme(choice);
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.dataset["theme"] = resolved;
  root.style.colorScheme = resolved;
}

export function setTheme(choice: ThemeChoice) {
  if (typeof window === "undefined") return;
  safeSetItem(THEME_STORAGE_KEY, choice);
  applyTheme(choice);
  window.dispatchEvent(new CustomEvent(THEME_EVENT));
}

export function useTheme() {
  const [choice, setChoice] = useState<ThemeChoice>("system");
  const [resolved, setResolved] = useState<"light" | "dark">("light");

  useEffect(() => {
    const sync = () => {
      const next = readStoredTheme();
      setChoice(next);
      setResolved(resolveTheme(next));
      applyTheme(next);
    };
    sync();
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    mql.addEventListener("change", sync);
    window.addEventListener(THEME_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      mql.removeEventListener("change", sync);
      window.removeEventListener(THEME_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const update = useCallback((next: ThemeChoice) => setTheme(next), []);
  return { theme: choice, resolvedTheme: resolved, setTheme: update };
}

/**
 * Blocking inline script: runs before first paint so there is no flash of the
 * wrong theme. Kept dependency-free and tiny.
 */
export const THEME_INIT_SCRIPT = `(function(){try{var t=localStorage.getItem('theme')||'system';var d=t==='dark'||(t==='system'&&window.matchMedia('(prefers-color-scheme: dark)').matches);if(location.pathname.indexOf('/admin')===0)d=false;var r=document.documentElement;r.classList.toggle('dark',d);r.dataset.theme=d?'dark':'light';r.style.colorScheme=d?'dark':'light';}catch(e){}})();`;
